import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaTimes, FaSave, FaPlus, FaTrash, FaGlobe, FaImages, FaFilm, FaList,
} from 'react-icons/fa';
import { MdFaceRetouchingNatural } from 'react-icons/md';
import { toast } from 'react-toastify';
import api from '../../api';
import Loader from '../../Components/Loader/Loader';
import Video from './Video';
import VideoClipsPanel from './VideoClipsPanel';
import ImageGrid from './ImageGrid';
import NarrationImagePanel from './NarrationImagePanel';
import IntroTeaserPanel from './IntroTeaserPanel';
import TransitionBannerPanel from './TransitionBannerPanel';
import { validateClipTag, validateSegmentTag, findUntaggedClipSegments, hasIntroTagAnywhere } from './tagValidation';
import SegmentAddMenu from './SegmentAddMenu';
import { clipTextFromTag, getClipSegmentSuggestions } from './clipSegmentUtils';
import { formatClipTimestamp, nudgeClipTimestamp, validateClipTimeUpdate, withFinishedClip } from './clipTimeUtils';
import { getPresetById, TEASER_PRESETS } from './teaserPresets';
import { DEFAULT_BANNER_CONFIG, normalizeBannerConfig, normalizeBannerConfigForExport } from './bannerPresets';
import './composition.css';
import './dreamfacecomposition.css';
import { getVideoSourceLabel } from './videoPlayback';
import BulkClipLinkImport, { BulkClipLinkAnchorButton } from './BulkClipLinkImport.jsx';
import {
  parseBulkVideoLinks,
  collectClipTagsInSection,
  buildBulkImportBundle,
  mergeClipsTree,
  insertSegmentsAt,
} from './bulkLinkImportUtils';
import {
  ENABLE_BULK_LINK_FRONTEND_RESOLVE,
  resolveBulkImportUrls,
} from './bulkLinkResolve';

const isClipText = (text) => {
  const t = (text || '').trim();
  return t.startsWith('[CLIP:') && t.endsWith(']');
};

const getClipToggleKey = (character, url) => `${character}::${url}`;

const getTeaserStorageKey = (user) =>
  `composition_intro_teaser_${user?.email || user?._id || user?.id || 'guest'}`;

const loadAccountTeaserDefault = (user) => {
  try {
    const raw = localStorage.getItem(getTeaserStorageKey(user));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const saveAccountTeaserDefault = (user, payload) => {
  localStorage.setItem(getTeaserStorageKey(user), JSON.stringify(payload));
};

export default function DreamFaceCompositionPanel({ jobId, open, onClose, user, jobStatus, orientation = 'landscape' }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkImportBusy, setBulkImportBusy] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkImportPlacement, setBulkImportPlacement] = useState({ mode: 'end', index: 0 });
  const [active, setActive] = useState('timeline');
  const [section, setSection] = useState(null);
  const [tagToggles, setTagToggles] = useState({});
  const [clipTagToggles, setClipTagToggles] = useState({});
  const [selectedMedia, setSelectedMedia] = useState({ url: '', character: '' });
  const [manualInput, setManualInput] = useState('00:00:00.00');
  const [isSavingStart, setIsSavingStart] = useState(true);
  const [clipEditTarget, setClipEditTarget] = useState(null);
  const [previewingClipIndex, setPreviewingClipIndex] = useState(null);
  const videoRef = useRef(null);

  const [teaserPresetId, setTeaserPresetId] = useState('breaking_news');
  const [introTeaserConfig, setIntroTeaserConfig] = useState(() => ({
    ...getPresetById('breaking_news').config,
  }));
  const [accountTeaserLabel, setAccountTeaserLabel] = useState('Breaking News XL');

  const [bannerPresetId, setBannerPresetId] = useState('blank');
  const [transitionBannerEnabled, setTransitionBannerEnabled] = useState(true);
  const [transitionBannerConfig, setTransitionBannerConfig] = useState(() => ({
    ...DEFAULT_BANNER_CONFIG,
  }));

  const [narrationImageMode, setNarrationImageMode] = useState('default');
  const [globalNarrationImage, setGlobalNarrationImage] = useState('');
  const [segmentNarrationImages, setSegmentNarrationImages] = useState({ intro: {} });

  const loadComposition = useCallback(async () => {
    if (!jobId || !user?.token) return;
    setLoading(true);
    try {
      const r = await api.get(`/dreamface/jobs/${jobId}/composition`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const sec = r.data.section || {};
      setSection({
        ...sec,
        segment: Array.isArray(sec.segment) ? sec.segment : [],
        clips: sec.clips || [],
        imageData: sec.imageData || [],
        section_characters: sec.section_characters || [],
        section_videos: sec.section_videos || [],
        section_images: sec.section_images || [],
      });
      const ni = r.data.narration_images || {};
      setNarrationImageMode(ni.mode || 'default');
      setGlobalNarrationImage(ni.globalImage || '');
      setSegmentNarrationImages(ni.perSegment || { intro: {} });
      const toggles = {};
      (sec.segment || []).forEach((_, i) => { toggles[i] = !!(sec.segment[i]?.tag); });
      setTagToggles(toggles);

      const introTeaser = r.data.intro_teaser;
      if (introTeaser?.config) {
        setTeaserPresetId(introTeaser.presetId || introTeaser.preset_id || 'breaking_news');
        setIntroTeaserConfig(introTeaser.config);
        const preset = TEASER_PRESETS.find(p => p.id === (introTeaser.presetId || introTeaser.preset_id));
        setAccountTeaserLabel(preset?.label || 'Custom');
      } else {
        const saved = loadAccountTeaserDefault(user);
        if (saved?.presetId && saved?.config) {
          setTeaserPresetId(saved.presetId);
          setIntroTeaserConfig(saved.config);
          const preset = TEASER_PRESETS.find(p => p.id === saved.presetId);
          setAccountTeaserLabel(saved.presetId === 'custom' ? 'Custom' : (preset?.label || 'Custom'));
        }
      }

      const transitionBanner = r.data.transition_banner;
      if (transitionBanner) {
        setTransitionBannerEnabled(transitionBanner.enabled !== false);
        setBannerPresetId(transitionBanner.presetId || transitionBanner.preset_id || 'blank');
        setTransitionBannerConfig(normalizeBannerConfig(transitionBanner.config || DEFAULT_BANNER_CONFIG));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load composition');
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [jobId, user?.token, user, onClose]);

  useEffect(() => {
    if (open) loadComposition();
  }, [open, loadComposition]);

  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || active !== 'videos') return undefined;
    const t = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 120);
    return () => clearTimeout(t);
  }, [open, active, selectedMedia.url]);

  useEffect(() => {
    setClipEditTarget(null);
  }, [selectedMedia.url, selectedMedia.character]);

  const updateSegment = (index, patch) => {
    setSection(prev => {
      const segs = [...(prev.segment || [])];
      segs[index] = { ...segs[index], ...patch };
      return { ...prev, segment: segs };
    });
  };

  const handleAddTag = (index, e) => {
    const tag = e.target.value;
    const result = validateSegmentTag({
      tag,
      sectionKey: 'intro',
      sections: [{ section: 'intro', segment: section?.segment || [] }],
      segmentIndex: index,
    });
    if (!result.ok) { toast.error(result.message); return; }
    updateSegment(index, { tag });
  };

  const insertClipSegmentAfter = useCallback((afterIndex, newSeg) => {
    setSection(prev => {
      const segs = [...(prev.segment || [])];
      segs.splice(afterIndex + 1, 0, newSeg);
      return { ...prev, segment: segs };
    });
    setTagToggles(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        const n = Number(k);
        if (n <= afterIndex) next[n] = prev[k];
        else next[n + 1] = prev[k];
      });
      next[afterIndex + 1] = !!(newSeg.tag || '').trim();
      return next;
    });
  }, []);

  const addClipSegment = (afterIndex) => {
    const id = `clip-${Date.now()}`;
    const char = section?.section_characters?.[0] || '';
    insertClipSegmentAfter(afterIndex, { character: char, text: clipTextFromTag(id), tag: '' });
    setTagToggles(prev => ({ ...prev, [afterIndex + 1]: true }));
    toast.info('Clip segment added — set a tag and add matching clip in Videos tab');
  };

  const addClipSegmentByTag = (afterIndex, tag) => {
    const trimmed = (tag || '').trim();
    if (!trimmed) return;
    const newIndex = afterIndex + 1;
    const result = validateSegmentTag({
      tag: trimmed,
      sectionKey: 'intro',
      sections: [{ section: 'intro', segment: section?.segment || [] }],
      segmentIndex: newIndex,
    });
    if (!result.ok) {
      toast.error(result.message);
      return;
    }
    const char = section?.section_characters?.[0] || section?.segment?.[afterIndex]?.character || '';
    insertClipSegmentAfter(afterIndex, {
      character: char,
      text: clipTextFromTag(trimmed),
      tag: trimmed,
    });
  };

  const dreamFaceClipSuggestions = useMemo(
    () => getClipSegmentSuggestions({ intro: section }, 'intro', section?.segment),
    [section],
  );

  const deleteSegmentAt = (index) => {
    const seg = section?.segment?.[index];
    if (!isClipText(seg?.text)) {
      toast.error('Narration segments cannot be removed — DreamFace audio is already generated');
      return;
    }
    setSection(prev => ({
      ...prev,
      segment: prev.segment.filter((_, i) => i !== index),
    }));
  };

  const handleImageSelect = useCallback((imageUrl, character) => {
    setSection(prev => {
      const imageData = [...(prev.imageData || [])];
      const idx = imageData.findIndex(x => x.characterName === character);
      if (idx !== -1) {
        const imgs = imageData[idx].images || [];
        imageData[idx] = {
          ...imageData[idx],
          images: imgs.includes(imageUrl)
            ? imgs.filter(u => u !== imageUrl)
            : [...imgs, imageUrl],
        };
      } else {
        imageData.push({ characterName: character, images: [imageUrl] });
      }
      return { ...prev, imageData };
    });
  }, []);

  const onClipSave = useCallback((startTime, endTime, character) => {
    setSection(prev => {
      const updatedClips = [...(prev.clips || [])];
      const charIdx = updatedClips.findIndex(c => c.characterName === character);
      const newClip = { startTime, endTime, mute: true };
      if (charIdx !== -1) {
        const charData = { ...updatedClips[charIdx] };
        const vids = [...charData.videos];
        const vidIdx = vids.findIndex(v => v.url === selectedMedia.url);
        if (vidIdx !== -1) {
          const video = { ...vids[vidIdx] };
          const vidclips = [...video.vidclips];
          if (endTime === null) vidclips.push(newClip);
          else vidclips[vidclips.length - 1] = { ...vidclips[vidclips.length - 1], endTime };
          vids[vidIdx] = { ...video, vidclips };
        } else {
          vids.push({ url: selectedMedia.url, vidclips: [newClip] });
        }
        charData.videos = vids;
        updatedClips[charIdx] = charData;
      } else {
        updatedClips.push({
          characterName: character,
          videos: [{ url: selectedMedia.url, vidclips: [newClip] }],
        });
      }
      return { ...prev, clips: updatedClips };
    });
  }, [selectedMedia.url]);

  const updateClipTime = useCallback((videoIndex, clipIndex, field, timeString) => {
    if (videoIndex < 0) return;

    setSection(prev => {
      const clips = JSON.parse(JSON.stringify(prev.clips || []));
      const cIdx = clips.findIndex(c => c.characterName === selectedMedia.character);
      if (cIdx === -1) return prev;
      const video = clips[cIdx].videos?.[videoIndex];
      const clip = video?.vidclips?.[clipIndex];
      if (!clip) return prev;

      const result = validateClipTimeUpdate(clip.startTime, clip.endTime, field, timeString);
      if (!result.ok) {
        toast.error(result.error);
        return prev;
      }

      video.vidclips[clipIndex][field] = result.value;
      return { ...prev, clips };
    });
  }, [selectedMedia.character]);

  const deleteClipAt = useCallback((videoIndex, clipIndex) => {
    if (videoIndex < 0) return;
    setSection(prev => {
      const clips = JSON.parse(JSON.stringify(prev.clips || []));
      const cIdx = clips.findIndex(c => c.characterName === selectedMedia.character);
      if (cIdx === -1) return prev;
      const videos = clips[cIdx].videos || [];
      const video = videos[videoIndex];
      if (!video) return prev;
      video.vidclips.splice(clipIndex, 1);
      if (video.vidclips.length === 0) videos.splice(videoIndex, 1);
      if (videos.length === 0) clips.splice(cIdx, 1);
      return { ...prev, clips };
    });
  }, [selectedMedia.character]);

  const applyCropToClips = useCallback((character, url, cropSpec, applyTo) => {
    setSection(prev => {
      const clips = JSON.parse(JSON.stringify(prev.clips || []));
      const cIdx = clips.findIndex(c => c.characterName === character);
      if (cIdx === -1) return prev;
      const videos = clips[cIdx].videos || [];
      const vidIdx = videos.findIndex(v => v.url === url);
      if (vidIdx === -1) return prev;
      const video = videos[vidIdx];
      video.vidclips = applyTo === 'all'
        ? video.vidclips.map(clip => ({ ...clip, crop: cropSpec }))
        : video.vidclips.map((clip, i) => (
          i === video.vidclips.length - 1 ? { ...clip, crop: cropSpec } : clip
        ));
      return { ...prev, clips };
    });
    toast.success(`Crop applied to ${applyTo === 'all' ? 'all clips' : 'last clip'}!`);
  }, []);

  const handleAddClipTag = (character, url, clipIdx, e) => {
    const tag = e.target.value;
    const result = validateClipTag({
      tag,
      sectionKey: 'intro',
      sections: [{ section: 'intro', segment: section?.segment || [] }],
      selectedData: { intro: section },
      exclude: { character, url, clipIndex: clipIdx },
    });
    if (!result.ok) { toast.error(result.message); return; }
    setSection(prev => {
      const clips = JSON.parse(JSON.stringify(prev.clips || []));
      const cIdx = clips.findIndex(c => c.characterName === character);
      if (cIdx === -1) return prev;
      const vIdx = clips[cIdx].videos.findIndex(v => v.url === url);
      if (vIdx === -1) return prev;
      clips[cIdx].videos[vIdx].vidclips[clipIdx].tag = tag;
      return { ...prev, clips };
    });
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!videoRef.current) return;
    const result = await videoRef.current.processManualEntry(manualInput);
    if (result?.success === 'Start time saved') setIsSavingStart(false);
    else if (result?.success === 'End time saved') setIsSavingStart(true);
    if (result?.error) toast.error(result.error);
  };

  const handleSeekToTimestamp = useCallback((timeString) => {
    if (!videoRef.current?.seekToTimestamp) {
      toast.error('Select a video to seek');
      return;
    }
    const result = videoRef.current.seekToTimestamp(timeString);
    if (!result?.ok) toast.error(result?.error || 'Could not seek to that time');
  }, []);

  const handleUseAll = useCallback(() => {
    const seconds = videoRef.current?.getDurationSeconds?.() || 0;
    if (!(seconds > 0.05)) {
      toast.error('Wait for the video to load, then click Use all.');
      return;
    }
    const end = formatClipTimestamp(seconds);
    if (!end || !selectedMedia.url || !selectedMedia.character) return;
    setSection(prev => ({
      ...prev,
      clips: withFinishedClip(prev.clips, selectedMedia.character, selectedMedia.url, '00:00:00.00', end),
    }));
    videoRef.current?.resetClipState?.();
    setIsSavingStart(true);
    toast.success(`Using the full clip · 00:00:00.00 → ${end}`);
  }, [selectedMedia]);

  const handleClipRangeEnd = useCallback(() => {
    setPreviewingClipIndex(null);
  }, []);

  const handlePreviewClip = useCallback((clipIdx) => {
    const charClips = section?.clips?.find(c => c.characterName === selectedMedia.character);
    const clip = charClips?.videos?.find(v => v.url === selectedMedia.url)?.vidclips?.[clipIdx];
    if (!clip?.startTime || !clip?.endTime) {
      toast.error('Set a start and end time before previewing');
      return;
    }
    if (previewingClipIndex === clipIdx) {
      videoRef.current?.stopClipRangePreview?.();
      setPreviewingClipIndex(null);
      return;
    }
    const result = videoRef.current?.playClipRange?.(clip.startTime, clip.endTime);
    if (!result?.ok) {
      setPreviewingClipIndex(null);
      toast.error(result?.error || 'Could not preview this clip');
      return;
    }
    setPreviewingClipIndex(clipIdx);
  }, [previewingClipIndex, section, selectedMedia]);

  const applyClipTimeFromVideo = useCallback((timeString) => {
    if (!clipEditTarget || !selectedMedia.character || !selectedMedia.url) return;

    const charClips = section?.clips?.find(c => c.characterName === selectedMedia.character);
    const vidIdx = charClips?.videos?.findIndex(v => v.url === selectedMedia.url) ?? -1;
    if (vidIdx < 0) return;

    const clip = charClips.videos[vidIdx].vidclips?.[clipEditTarget.clipIndex];
    if (!clip) return;

    const result = validateClipTimeUpdate(
      clip.startTime,
      clip.endTime,
      clipEditTarget.field,
      timeString,
    );
    if (!result.ok) {
      toast.error(result.error);
      return;
    }

    updateClipTime(vidIdx, clipEditTarget.clipIndex, clipEditTarget.field, result.value);
    setClipEditTarget(null);
    videoRef.current?.releaseTimestampEdit?.();
  }, [clipEditTarget, section, selectedMedia, updateClipTime]);

  const handleActivateClipField = useCallback((clipIdx, field) => {
    videoRef.current?.releaseTimestampEdit?.();
    setPreviewingClipIndex(null);

    const charClips = section?.clips?.find(c => c.characterName === selectedMedia.character);
    const vidIdx = charClips?.videos?.findIndex(v => v.url === selectedMedia.url) ?? -1;
    const clip = vidIdx >= 0 ? charClips?.videos?.[vidIdx]?.vidclips?.[clipIdx] : null;
    const currentTime = clip?.[field] || null;

    setClipEditTarget({ clipIndex: clipIdx, field, previewTime: currentTime, originalTime: currentTime });

    if (currentTime && videoRef.current?.seekToTimestamp) {
      videoRef.current.seekToTimestamp(currentTime);
    }
  }, [section, selectedMedia]);

  const handleSaveClipField = useCallback(() => {
    if (!clipEditTarget?.previewTime) return;
    applyClipTimeFromVideo(clipEditTarget.previewTime);
  }, [clipEditTarget, applyClipTimeFromVideo]);

  const handleCancelClipField = useCallback(() => {
    const original = clipEditTarget?.originalTime;
    setClipEditTarget(null);
    setPreviewingClipIndex(null);
    videoRef.current?.releaseTimestampEdit?.();
    if (original && videoRef.current?.seekToTimestamp) {
      videoRef.current.seekToTimestamp(original);
    }
  }, [clipEditTarget]);

  const handleClipEditPlayhead = useCallback((timeString) => {
    setClipEditTarget(prev => {
      if (!prev || prev.previewTime === timeString) return prev;
      return { ...prev, previewTime: timeString };
    });
  }, []);

  const handleNudgeClipTime = useCallback((clipIdx, field, deltaSec) => {
    const charClips = section?.clips?.find(c => c.characterName === selectedMedia.character);
    const clip = charClips?.videos?.find(v => v.url === selectedMedia.url)?.vidclips?.[clipIdx];
    const live = clipEditTarget?.clipIndex === clipIdx && clipEditTarget?.field === field
      ? clipEditTarget.previewTime
      : null;
    const base = live || videoRef.current?.getPlayheadTimestamp?.() || clip?.[field];
    const next = nudgeClipTimestamp(base, deltaSec);
    if (!next) return;
    const result = videoRef.current?.seekToTimestamp?.(next);
    if (result && !result?.ok) toast.error(result?.error || 'Could not seek the video');
  }, [clipEditTarget, section, selectedMedia]);

  const activeClipTagToggles = clipTagToggles[getClipToggleKey(selectedMedia.character, selectedMedia.url)] || {};

  const introTagActive = useMemo(
    () => hasIntroTagAnywhere({
      sections: [{ section: 'intro', segment: section?.segment || [] }],
      selectedData: { intro: section || {} },
    }),
    [section],
  );

  const handleSaveAccountTeaserDefault = ({ presetId, config }) => {
    saveAccountTeaserDefault(user, { presetId, config });
    const preset = TEASER_PRESETS.find(p => p.id === presetId);
    setAccountTeaserLabel(presetId === 'custom' ? 'Custom' : (preset?.label || 'Custom'));
  };

  const handleBulkClipLinkImport = useCallback(async ({ character, linksText, tagPrefix, placement }) => {
    const urls = parseBulkVideoLinks(linksText);
    if (!urls.length) {
      toast.error('Add at least one http(s) link.');
      return;
    }
    if (!section?.section_characters?.includes(character)) {
      toast.error('Character not in intro section.');
      return;
    }

    if (!ENABLE_BULK_LINK_FRONTEND_RESOLVE) {
      toast.error('Bulk link resolve is disabled in bulkLinkResolve.js');
      return;
    }

    setBulkImportBusy(true);
    try {
      const resolved = await resolveBulkImportUrls(api, urls, user.token, {
        onItemError: (i, _linkUrl, err) => {
          const msg = err?.response?.data?.error || err?.message || 'failed';
          toast.error(`Link ${i + 1}: ${msg}`);
        },
      });

      if (!resolved.length) return;

      const usedTags = collectClipTagsInSection(section?.clips);
      (section?.segment || []).forEach(seg => {
        const t = (seg.tag || '').trim();
        if (t) usedTags.add(t);
      });

      const { videos, segments: newSegs } = buildBulkImportBundle(
        resolved,
        character,
        tagPrefix,
        usedTags,
      );
      if (!newSegs.length) return;

      const existing = section?.segment || [];
      const insertAt = (() => {
        const mode = placement?.mode || 'end';
        const anchor = Number.isFinite(placement?.index) ? placement.index : existing.length - 1;
        if (mode === 'end') return existing.length;
        if (mode === 'after') return Math.min(existing.length, Math.max(0, anchor + 1));
        return Math.min(existing.length, Math.max(0, anchor));
      })();

      setSection(prev => ({
        ...prev,
        segment: insertSegmentsAt(prev?.segment, newSegs, placement),
        clips: mergeClipsTree(prev?.clips, character, videos),
      }));

      setTagToggles(prev => {
        const next = {};
        Object.keys(prev).forEach(k => {
          const n = Number(k);
          if (n < insertAt) next[n] = prev[k];
          else next[n + newSegs.length] = prev[k];
        });
        newSegs.forEach((s, i) => {
          next[insertAt + i] = !!(s.tag || '').trim();
        });
        return next;
      });

      toast.success(`Imported ${newSegs.length} full-length clip(s) onto timeline and Videos.`);
    } finally {
      setBulkImportBusy(false);
    }
  }, [section, user.token]);

  const handleSave = async () => {
    if (!section) return false;
    const untagged = findUntaggedClipSegments([{ section: 'intro', segment: section.segment }]);
    if (untagged.length) {
      toast.error('Tag every [CLIP:…] segment before saving');
      return false;
    }
    if (narrationImageMode === 'global' && !globalNarrationImage.trim()) {
      toast.error('Upload or paste a global narration image');
      return false;
    }
    setSaving(true);
    try {
      await api.put(
        `/dreamface/jobs/${jobId}/composition`,
        {
          section,
          narration_images: {
            mode: narrationImageMode,
            globalImage: narrationImageMode === 'global' ? globalNarrationImage : undefined,
            perSegment: narrationImageMode === 'per_segment' ? segmentNarrationImages : undefined,
          },
          intro_teaser: {
            presetId: teaserPresetId,
            config: introTeaserConfig,
          },
          transition_banner: {
            enabled: transitionBannerEnabled,
            presetId: bannerPresetId,
            config: normalizeBannerConfigForExport(transitionBannerConfig, orientation),
          },
        },
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      toast.success('Composition saved');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="dfc-overlay"
      role="dialog"
      aria-modal="true"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dfc-panel composition-panel" onClick={e => e.stopPropagation()}>
        <div className="dfc-sheet-handle" aria-hidden="true" />

        <header className="dfc-header composition-header">
          <div className="dfc-header__row">
            <div className="dfc-header__title">
              <MdFaceRetouchingNatural className="dfc-header__icon" aria-hidden="true" />
              <div className="dfc-header__copy">
                <h2>Edit Composition</h2>
                <p className="dfc-header__subtitle">Tags, clips, images, teaser &amp; banner</p>
              </div>
            </div>
            <div className="dfc-header__actions composition-btns">
              <button
                type="button"
                className="btn-save dfc-save-btn"
                onClick={handleSave}
                disabled={saving || loading}
              >
                <FaSave aria-hidden="true" />
                <span className="dfc-save-btn__label">{saving ? 'Saving…' : 'Save'}</span>
              </button>
              <button type="button" className="dfc-close" onClick={onClose} aria-label="Close editor">
                <FaTimes />
              </button>
            </div>
          </div>
        </header>

        <div className="dfc-chrome">
          <div className="talking-heads-banner dfc-banner">
            <MdFaceRetouchingNatural className="banner-icon" aria-hidden="true" />
            <div className="banner-text">
              <strong>Narration locked</strong>
              <span className="dfc-banner__detail">
                Save here, close, then <strong>Generate Video</strong>.
              </span>
            </div>
          </div>

          <div className="dfc-nav panel-header">
            <select value={active} onChange={e => setActive(e.target.value)} aria-label="Composition view">
              <option value="timeline">⏱ Timeline</option>
              <option value="images">🖼 Images</option>
              <option value="videos">🎬 Videos</option>
              <option value="teaser">📺 Intro Teaser</option>
              <option value="banner">🎬 Transition Banner</option>
              <option value="narrationImages">🖼 Narration Images</option>
            </select>
          </div>
        </div>

        <div className="dfc-main compostion-main-panel">
          {loading ? (
            <div className="dfc-loading"><Loader /></div>
          ) : (
            <>
              {active === 'timeline' && (
                <div className="timeline dfc-timeline">
                  <BulkClipLinkImport
                    characters={section?.section_characters || []}
                    defaultCharacter={section?.section_characters?.[0] || ''}
                    segments={section?.segment || []}
                    busy={bulkImportBusy}
                    onImport={handleBulkClipLinkImport}
                    open={bulkImportOpen}
                    onOpenChange={setBulkImportOpen}
                    placement={bulkImportPlacement}
                    onPlacementChange={setBulkImportPlacement}
                  />
                  {(section?.segment || []).map((seg, index) => (
                    <div
                      key={index}
                      className={`segment ${isClipText(seg.text) ? 'dfc-segment--clip' : 'dfc-segment--narration'}`}
                    >
                      <div className="segment-head">
                        <span className={`dfc-type-pill ${isClipText(seg.text) ? 'dfc-type-pill--clip' : 'dfc-type-pill--narration'}`}>
                          {isClipText(seg.text) ? 'CLIP' : 'Narration'}
                        </span>

                        <select
                          value={seg.character || ''}
                          onChange={e => updateSegment(index, { character: e.target.value })}
                        >
                          {(section?.section_characters || []).map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>

                        <label>
                          <input
                            className="toggle-checkbox"
                            type="checkbox"
                            checked={!!tagToggles[index]}
                            onChange={() => setTagToggles(p => ({ ...p, [index]: !p[index] }))}
                          />
                          <div className="toggle-slot">
                            <div className="sun-icon-wrapper" />
                            <div className="toggle-button" />
                            <div className="moon-icon-wrapper" />
                          </div>
                        </label>

                        {tagToggles[index] && (
                          <div className="segment-tag-input">
                            <div className="tag-input">
                              <input
                                className="to-do-input"
                                placeholder="Tag this segment…"
                                value={seg.tag || ''}
                                type="text"
                                onChange={e => handleAddTag(index, e)}
                              />
                              <div className="todo-line" />
                            </div>
                          </div>
                        )}
                      </div>

                      <textarea
                        readOnly
                        value={seg.text || ''}
                        className="dfc-text-locked"
                        title="Narration locked — cannot edit after DreamFace audio generation"
                        placeholder="Segment text…"
                      />

                      <div className="segment-controls">
                        {isClipText(seg.text) && (
                          <div className="control-button" onClick={() => deleteSegmentAt(index)} title="Remove clip segment">
                            <FaTrash className="icon delete-icon" />
                          </div>
                        )}
                        <BulkClipLinkAnchorButton
                          index={index}
                          mode="before"
                          disabled={bulkImportBusy}
                          onActivate={(p) => {
                            setBulkImportPlacement(p);
                            setBulkImportOpen(true);
                          }}
                        />
                        <BulkClipLinkAnchorButton
                          index={index}
                          mode="after"
                          disabled={bulkImportBusy}
                          onActivate={(p) => {
                            setBulkImportPlacement(p);
                            setBulkImportOpen(true);
                          }}
                        />
                        <SegmentAddMenu
                          suggestions={dreamFaceClipSuggestions}
                          onAddGenericClip={() => addClipSegment(index)}
                          onAddClipTag={(tag) => addClipSegmentByTag(index, tag)}
                          showBlankOption={false}
                          showGenericClipOption
                          title="Add clip segment after"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {active === 'images' && (
                <div className="media dfc-media">
                  {(section?.section_images || []).map((charBlock, idx) => (
                    <div key={idx} className="dfc-char-block">
                      <div className="character-link-container">
                        <div className="charcter-link">
                          <FaGlobe />
                          <span>{charBlock.character}</span>
                        </div>
                      </div>
                      <ImageGrid
                        images={charBlock.images || []}
                        character={charBlock.character}
                        selectedData={{ intro: section }}
                        sectionName="intro"
                        handleImageSelect={handleImageSelect}
                      />
                    </div>
                  ))}
                  {!section?.section_images?.length && (
                    <p className="dfc-empty">No scraped images on this job — use Narration Images for overrides.</p>
                  )}
                </div>
              )}

              {active === 'videos' && (
                <div className="dfc-videos-shell dfc-videos-shell--fill">
                  <aside className="dfc-video-sidebar section-data">
                    {(section?.section_videos || []).map((videoData, index) => (
                      <div key={index}>
                        <div className="character-link-container">
                          <div className="charcter-link">
                            <FaGlobe />
                            <span>{videoData.Character}</span>
                          </div>
                        </div>
                        <div className="video-link-side-panel">
                          {(videoData.videos || []).map((data, vIdx) => (
                            <div
                              key={vIdx}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedMedia({ url: data.url, character: videoData.Character })}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedMedia({ url: data.url, character: videoData.Character });
                                }
                              }}
                              className={selectedMedia.url === data.url ? 'existing-url' : ''}
                              style={{
                                borderLeft: selectedMedia.url === data.url ? '2px solid var(--accent)' : '2px solid transparent',
                              }}
                            >
                              <h3>{getVideoSourceLabel(data.url).toUpperCase()}</h3>
                              <p>{data.title || data.url}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </aside>

                  <div className="dfc-video-content">
                    {selectedMedia.url ? (() => {
                      const charClips = section?.clips?.find(c => c.characterName === selectedMedia.character);
                      const vidIdx = charClips?.videos?.findIndex(v => v.url === selectedMedia.url) ?? -1;
                      const vidData = vidIdx >= 0 ? charClips.videos[vidIdx] : null;

                      return (
                        <div className="composition-videos dfc-composition-videos">
                          <div className="video-section dfc-video-section">
                            <Video
                              key={`${selectedMedia.character}::${selectedMedia.url}`}
                              ref={videoRef}
                              videoUrl={selectedMedia.url}
                              onClipSave={onClipSave}
                              character={selectedMedia.character}
                              onPhaseChange={setIsSavingStart}
                              onCropApply={(cropSpec, applyTo) =>
                                applyCropToClips(selectedMedia.character, selectedMedia.url, cropSpec, applyTo)
                              }
                              clipEditTarget={clipEditTarget}
                              onApplyClipTimeFromVideo={applyClipTimeFromVideo}
                              onClipEditPlayhead={handleClipEditPlayhead}
                              onCancelClipEdit={handleCancelClipField}
                              onClipRangeEnd={handleClipRangeEnd}
                            />
                          </div>

                          <div
                            className="clip-data-section"
                            onClick={e => e.stopPropagation()}
                            onTouchEnd={e => e.stopPropagation()}
                          >
                            <VideoClipsPanel
                              clips={vidData?.vidclips || []}
                              previewingClipIndex={previewingClipIndex}
                              onPreviewClip={handlePreviewClip}
                              onUseAll={handleUseAll}
                              activeField={clipEditTarget}
                              onActivateField={handleActivateClipField}
                              onSaveField={handleSaveClipField}
                              onCancelField={handleCancelClipField}
                              onUpdateClipTime={(clipIdx, field, val) =>
                                updateClipTime(vidIdx, clipIdx, field, val)
                              }
                              onDeleteClip={clipIdx => {
                                setClipEditTarget(null);
                                deleteClipAt(vidIdx, clipIdx);
                              }}
                              onSeek={handleSeekToTimestamp}
                              onNudgeClipTime={handleNudgeClipTime}
                              showTag
                              clipTagToggles={activeClipTagToggles}
                              onClipTagToggle={clipIdx => {
                                const key = getClipToggleKey(selectedMedia.character, selectedMedia.url);
                                setClipTagToggles(prev => ({
                                  ...prev,
                                  [key]: { ...(prev[key] || {}), [clipIdx]: !prev[key]?.[clipIdx] },
                                }));
                              }}
                              onClipTagChange={(clipIdx, tag) => {
                                const fakeEvent = { target: { value: tag } };
                                handleAddClipTag(selectedMedia.character, selectedMedia.url, clipIdx, fakeEvent);
                              }}
                              manualInput={manualInput}
                              onManualInputChange={e => setManualInput(e.target.value)}
                              isSavingStart={isSavingStart}
                              onManualSubmit={handleManualSubmit}
                            />
                          </div>
                        </div>
                      );
                    })() : (
                      <p className="dfc-empty">Select a video from the sidebar</p>
                    )}
                  </div>
                </div>
              )}

              {active === 'teaser' && (
                <IntroTeaserPanel
                  teaserConfig={introTeaserConfig}
                  setTeaserConfig={setIntroTeaserConfig}
                  presetId={teaserPresetId}
                  setPresetId={setTeaserPresetId}
                  introTagActive={introTagActive}
                  orientation={orientation}
                  accountDefaultLabel={accountTeaserLabel}
                  onSaveAccountDefault={handleSaveAccountTeaserDefault}
                />
              )}

              {active === 'banner' && (
                <TransitionBannerPanel
                  bannerEnabled={transitionBannerEnabled}
                  setBannerEnabled={setTransitionBannerEnabled}
                  bannerConfig={transitionBannerConfig}
                  setBannerConfig={setTransitionBannerConfig}
                  presetId={bannerPresetId}
                  setPresetId={setBannerPresetId}
                  orientation={orientation}
                  user={user}
                  userToken={user?.token}
                />
              )}

              {active === 'narrationImages' && (
                <NarrationImagePanel
                  narrationImageMode={narrationImageMode}
                  setNarrationImageMode={setNarrationImageMode}
                  globalNarrationImage={globalNarrationImage}
                  setGlobalNarrationImage={setGlobalNarrationImage}
                  segmentNarrationImages={segmentNarrationImages}
                  setSegmentNarrationImages={setSegmentNarrationImages}
                  currentSection={section}
                  sectionName="intro"
                  userToken={user?.token}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
